import { MEMBER_ROLE } from 'common/selectors/auth';
import { SidebarNavItem } from 'common/types/SidebarNavItem';

/**
 * Filter navigation data based on user roles, permissions, and feature flags
 *
 * @param isSuperuser - Whether the current user is a superuser
 * @param isStaff - Whether the current user is a staff member
 * @param employeeType - The employee type of the current user
 * @param features - Array of feature flags with their enabled status
 * @param assessmentPeriodStatus - The status of the current assessment period
 * @param organizationServices - The services enabled for the current organization
 * @returns Filtered navigation data with additional properties
 */
export type OrganizationOption = {
  key: string;
  enabled?: boolean;
  config?: Record<string, any>;
};

export const filterMainNavData = (
  NavData: SidebarNavItem[],
  isSuperuser: boolean,
  isStaff: boolean,
  employeeType: number | string | undefined,
  features: Array<{ name: string; enabled: boolean }>,
  areAnyAssessmentPeriodsClosed: boolean | undefined = false,
  hasEmployee: boolean = true,
  // Whether the user (if a team leader) has any team projects assigned across assessment periods
  hasTeamProjectsAssigned: boolean = true,
  organizationOptions: OrganizationOption[] = []
) => {
  const isDirector = employeeType === MEMBER_ROLE.DIRECTOR;
  const teamLeaderDashboardOption = organizationOptions.find(option => option.key === 'team_leader_dashboard');
  const isTeamLeaderDashboardEnabled = Boolean(teamLeaderDashboardOption?.enabled);
  const teamLeaderDashboardAudience =
    (teamLeaderDashboardOption?.config?.audience as string) || 'all_leaders';
  const pulseSurveysOption = organizationOptions.find(option => option.key === 'pulse_surveys');
  const isPulseSurveysEnabled = pulseSurveysOption?.enabled ?? true;

  const filterChildren = (children: SidebarNavItem[] | undefined): SidebarNavItem[] | undefined => {
    if (!children) return undefined;
    
    return children.filter(child =>
      ((child.superuser && isSuperuser) ||
        (child.staff && isStaff) ||
        (child.regular_member && employeeType === MEMBER_ROLE.REGULAR_USER) ||
        (child.team_admin && employeeType === MEMBER_ROLE.TEAM_LEADER) ||
        (child.director && isDirector) ||
        (child.org_admin && employeeType === MEMBER_ROLE.ORG_ADMIN)) &&
      (!child.feature_flag ||
        features.some(feature => feature.name === child.feature_flag && feature.enabled)) &&
      ((child.requires_closed_AP && areAnyAssessmentPeriodsClosed) ||
        !child.requires_closed_AP)
    );
  };

  return NavData.filter(
    sidebarItem =>
      ((sidebarItem.superuser && isSuperuser) ||
        (sidebarItem.staff && isStaff) ||
        (sidebarItem.regular_member && employeeType === MEMBER_ROLE.REGULAR_USER) ||
        (sidebarItem.team_admin && employeeType === MEMBER_ROLE.TEAM_LEADER) ||
        (sidebarItem.director && employeeType === MEMBER_ROLE.DIRECTOR) ||
        (sidebarItem.org_admin && employeeType === MEMBER_ROLE.ORG_ADMIN)) &&
      (!sidebarItem.feature_flag ||
        features.some(feature => feature.name === sidebarItem.feature_flag && feature.enabled)) &&
      // If team leaderboard is disabled for the organization, hide the leaderboard nav item entirely.
      !(sidebarItem.id === 'leaderboard' && !isTeamLeaderDashboardEnabled) &&
      // If pulse surveys are disabled for the organization, hide the pulse nav item.
      !(sidebarItem.id === 'pulse-surveys' && !isPulseSurveysEnabled) &&
      // If the leaderboard is restricted to org admins only, hide it for team leaders.
      !(sidebarItem.id === 'leaderboard' &&
        teamLeaderDashboardAudience === 'organization_admin_only' &&
        employeeType === MEMBER_ROLE.TEAM_LEADER) &&
      // If user is a team leader and they have no assigned team projects, suppress the culture-hub team projects nav item
      !(
        employeeType === MEMBER_ROLE.TEAM_LEADER &&
        !hasTeamProjectsAssigned &&
        // match the culture-hub team projects link specifically to avoid hiding admin 'team-projects'
        (sidebarItem.link || '').includes('/culture-hub/team-projects')
      )
  )
    .filter(
      sidebarItem =>
        // @ts-ignore
        (sidebarItem.requires_closed_AP && areAnyAssessmentPeriodsClosed) ||
        !sidebarItem.requires_closed_AP
    )
    .filter(
      sidebarItem => {
        // If user is staff/superuser with no employee record, they cannot access Culture Hub.
        // In that case, explicitly exclude the Culture Hub nav item but DO NOT hide legacy dashboard items.
        if ((isSuperuser || isStaff) && !hasEmployee) {
          if (sidebarItem.id === 'culture-hub') return false;
          return true;
        }
        return true;
      }
    )
    .map(sidebarItem => {
      let isDisabled = false;
      
      // Filter children if they exist
      const filteredChildren = filterChildren(sidebarItem.children);

      return {
        isDisabled,
        sidebarItem: {
          ...sidebarItem,
          children: filteredChildren,
        },
      };
    });
};

export const filterSettingsNavData = (
  NavData: SidebarNavItem[],
  isSuperuser: boolean,
  isStaff: boolean,
  isRegularUser: boolean,
  features: Array<{ name: string; enabled: boolean }>
) =>
  NavData.filter(
    sidebarItem =>
      ((isSuperuser && sidebarItem.superuser) ||
        (isStaff &&
          sidebarItem.staff &&
          !isRegularUser)) &&
      (!sidebarItem.feature_flag ||
        features.some(feature => feature.name === sidebarItem.feature_flag && feature.enabled))
  ).map(sidebarItem => {
    let isDisabled = false;

    return {
      isDisabled,
      sidebarItem,
    };
  });
